import { Badge } from '@/components/ui/shadcn/badge';
import { Button } from '@/components/ui/shadcn/button';
import { Input } from '@/components/ui/shadcn/input';
import { Label } from '@/components/ui/shadcn/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/shadcn/select';
import { Textarea } from '@/components/ui/shadcn/textarea';
import { useTranslations } from '@/hooks/useTranslations';
import ShowLayout from '@/layouts/show-layout';
import { type PageProps } from '@/types';
import { Head, Link, useForm, usePage } from '@inertiajs/react';
import { ArrowLeft, CalendarClock, ClipboardList, ExternalLink, Globe, Monitor, Paperclip, ShieldAlert, UserRound } from 'lucide-react';
import { route } from 'ziggy-js';

interface BugReportShowPageProps extends PageProps {
    report: {
        id: string;
        public_id: string | null;
        description: string;
        expected_behavior: string | null;
        actual_behavior: string | null;
        status: string;
        severity_suggested: string | null;
        severity_final: string | null;
        priority: string | null;
        category: string | null;
        module: string | null;
        blocking_work: boolean;
        url: string | null;
        path: string | null;
        route_name: string | null;
        page_title: string | null;
        entity_type: string | null;
        entity_id: string | null;
        query_params_sanitized: Record<string, unknown> | null;
        referrer: string | null;
        reporter_name: string | null;
        reporter_email: string | null;
        reporter_roles: string[] | null;
        browser_name: string | null;
        browser_version: string | null;
        os_name: string | null;
        device_type: string | null;
        screen_width: number | null;
        screen_height: number | null;
        viewport_width: number | null;
        viewport_height: number | null;
        timezone: string | null;
        locale: string | null;
        user_agent: string | null;
        internal_notes: string | null;
        resolution_notes: string | null;
        github_issue_number: number | null;
        github_issue_url: string | null;
        github_synced_at: string | null;
        assigned_to_user_id: string | null;
        duplicate_of_report_id: string | null;
        reporter: { id: string; name: string; email: string } | null;
        assignee: { id: string; name: string; email: string } | null;
        duplicate_of: { id: string; public_id: string | null } | null;
        created_at: string | null;
        updated_at: string | null;
        attachments: Array<{
            id: string;
            file_name: string;
            mime_type: string | null;
            file_size: number | null;
            uploaded_by: string | null;
            created_at: string | null;
            download_url: string;
        }>;
        events: Array<{
            id: string;
            event_type: string;
            old_value: Record<string, unknown> | null;
            new_value: Record<string, unknown> | null;
            note: string | null;
            actor: string | null;
            created_at: string | null;
        }>;
    };
    options: {
        statuses: string[];
        severities: string[];
        priorities: string[];
        users: Array<{ id: string; name: string }>;
    };
    [key: string]: unknown;
}

const statusBadgeClassMap: Record<string, string> = {
    new: 'bg-info-subtle text-info-subtle-foreground border-info-subtle-foreground/30',
    in_review: 'bg-cat-indigo text-cat-indigo-foreground border-cat-indigo-foreground/30',
    need_more_info: 'bg-warning-subtle text-warning-subtle-foreground border-warning-subtle-foreground/30',
    confirmed: 'bg-info-subtle text-info-subtle-foreground border-info-subtle-foreground/30',
    in_progress: 'bg-cat-purple text-cat-purple-foreground border-cat-purple-foreground/30',
    resolved: 'bg-success-subtle text-success-subtle-foreground border-success-subtle-foreground/30',
    duplicate: 'bg-cat-gray text-cat-gray-foreground border-cat-gray-foreground/30',
    rejected: 'bg-danger-subtle text-danger-subtle-foreground border-danger-subtle-foreground/30',
    archived: 'bg-cat-gray text-cat-gray-foreground border-cat-gray-foreground/30',
};

const severityBadgeClassMap: Record<string, string> = {
    low: 'bg-success-subtle text-success-subtle-foreground border-transparent',
    medium: 'bg-warning-subtle text-warning-subtle-foreground border-transparent',
    high: 'bg-warning-subtle text-warning-subtle-foreground border-transparent',
    critical: 'bg-danger-subtle text-danger-subtle-foreground border-transparent',
};

const priorityBadgeClassMap: Record<string, string> = {
    P0: 'bg-danger-subtle text-danger-subtle-foreground border-danger-subtle-foreground/30',
    P1: 'bg-warning-subtle text-warning-subtle-foreground border-warning-subtle-foreground/30',
    P2: 'bg-warning-subtle text-warning-subtle-foreground border-warning-subtle-foreground/30',
    P3: 'bg-info-subtle text-info-subtle-foreground border-info-subtle-foreground/30',
};

function DetailRow({ label, children }: { label: string; children: React.ReactNode }) {
    return (
        <div className="grid grid-cols-[140px_1fr] items-start gap-2 py-2.5 sm:grid-cols-[180px_1fr] sm:gap-4 sm:py-3">
            <div className="text-muted-foreground pt-0.5 text-sm">{label}</div>
            <div className="text-sm font-medium">{children}</div>
        </div>
    );
}

export default function BugReportShowPage() {
    const { props } = usePage<BugReportShowPageProps>();
    const { report, options } = props;
    const { t } = useTranslations();

    const { data, setData, patch, processing } = useForm({
        status: report.status,
        severity_final: report.severity_final ?? '',
        priority: report.priority ?? '',
        assigned_to_user_id: report.assigned_to_user_id ?? '',
        internal_notes: report.internal_notes ?? '',
        resolution_notes: report.resolution_notes ?? '',
        duplicate_of_report_id: report.duplicate_of_report_id ?? '',
    });

    const submit = () => {
        patch(route('admin.bug-reports.update', report.id), { preserveScroll: true });
    };

    const breadcrumbs = [
        { title: 'Bug Reports', href: '/admin/bug-reports' },
        { title: report.public_id ?? report.id.slice(0, 8), href: `/admin/bug-reports/${report.id}` },
    ];

    const sidebar = (
        <div className="bg-card rounded-xl border p-5">
            <h3 className="mb-4 text-sm font-semibold tracking-wide uppercase">Admin Actions</h3>
            <div className="space-y-4">
                <div className="space-y-2">
                    <Label className="inline-flex items-center gap-2 text-sm">
                        <ShieldAlert className="h-4 w-4" />
                        Status
                    </Label>
                    <Select value={data.status} onValueChange={(value) => setData('status', value)}>
                        <SelectTrigger className="w-full">
                            <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                            {options.statuses.map((status) => (
                                <SelectItem key={status} value={status}>
                                    {status}
                                </SelectItem>
                            ))}
                        </SelectContent>
                    </Select>
                </div>

                <div className="space-y-2">
                    <Label className="text-sm">Final severity</Label>
                    <Select value={data.severity_final || 'none'} onValueChange={(value) => setData('severity_final', value === 'none' ? '' : value)}>
                        <SelectTrigger className="w-full">
                            <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                            <SelectItem value="none">Not set</SelectItem>
                            {options.severities.map((s) => (
                                <SelectItem key={s} value={s}>
                                    {s}
                                </SelectItem>
                            ))}
                        </SelectContent>
                    </Select>
                </div>

                <div className="space-y-2">
                    <Label className="text-sm">Priority</Label>
                    <Select value={data.priority || 'none'} onValueChange={(value) => setData('priority', value === 'none' ? '' : value)}>
                        <SelectTrigger className="w-full">
                            <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                            <SelectItem value="none">Not set</SelectItem>
                            {options.priorities.map((p) => (
                                <SelectItem key={p} value={p}>
                                    {p}
                                </SelectItem>
                            ))}
                        </SelectContent>
                    </Select>
                </div>

                <div className="space-y-2">
                    <Label className="inline-flex items-center gap-2 text-sm">
                        <UserRound className="h-4 w-4" />
                        Assignee
                    </Label>
                    <Select
                        value={data.assigned_to_user_id || 'none'}
                        onValueChange={(value) => setData('assigned_to_user_id', value === 'none' ? '' : value)}
                    >
                        <SelectTrigger className="w-full">
                            <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                            <SelectItem value="none">Unassigned</SelectItem>
                            {options.users.map((u) => (
                                <SelectItem key={u.id} value={u.id}>
                                    {u.name}
                                </SelectItem>
                            ))}
                        </SelectContent>
                    </Select>
                </div>

                <div className="space-y-2">
                    <Label className="text-sm">Internal notes</Label>
                    <Textarea value={data.internal_notes} onChange={(event) => setData('internal_notes', event.target.value)} rows={4} />
                </div>

                <div className="space-y-2">
                    <Label className="text-sm">Resolution notes</Label>
                    <Textarea value={data.resolution_notes} onChange={(event) => setData('resolution_notes', event.target.value)} rows={4} />
                </div>

                <div className="space-y-2">
                    <Label className="inline-flex items-center gap-2 text-sm">
                        <CalendarClock className="h-4 w-4" />
                        Duplicate of (UUID)
                    </Label>
                    <Input
                        value={data.duplicate_of_report_id}
                        onChange={(event) => setData('duplicate_of_report_id', event.target.value)}
                        placeholder="UUID"
                    />
                </div>

                <Button type="button" className="w-full" onClick={submit} disabled={processing}>
                    {processing ? 'Saving...' : 'Save changes'}
                </Button>
            </div>
        </div>
    );

    return (
        <>
            <Head title={report.public_id ?? 'Bug Report'} />
            <ShowLayout breadcrumbs={breadcrumbs} title={report.public_id ?? 'Bug Report'} sidebar={sidebar} mobileSidebarTitle="Admin Actions">
                <div className="space-y-4">
                    <div className="flex items-center gap-3">
                        <Button variant="ghost" size="sm" asChild>
                            <Link href={route('admin.bug-reports.index')}>
                                <ArrowLeft className="h-4 w-4" />
                                Bug Reports
                            </Link>
                        </Button>
                    </div>

                    <div className="bg-card rounded-xl border p-5">
                        <div className="mb-4 flex flex-wrap items-center gap-2">
                            <Badge variant="outline" className={statusBadgeClassMap[report.status] ?? ''}>
                                {report.status}
                            </Badge>
                            {(report.severity_final || report.severity_suggested) && (
                                <Badge
                                    variant="outline"
                                    className={severityBadgeClassMap[report.severity_final ?? report.severity_suggested ?? ''] ?? ''}
                                >
                                    {report.severity_final ?? report.severity_suggested}
                                </Badge>
                            )}
                            {report.priority && (
                                <Badge variant="outline" className={priorityBadgeClassMap[report.priority] ?? ''}>
                                    {report.priority}
                                </Badge>
                            )}
                            <Badge
                                variant="outline"
                                className={
                                    report.blocking_work
                                        ? 'bg-danger-subtle text-danger-subtle-foreground border-transparent'
                                        : 'bg-success-subtle text-success-subtle-foreground border-transparent'
                                }
                            >
                                {report.blocking_work ? 'Blocking' : 'Non-blocking'}
                            </Badge>
                        </div>

                        <h3 className="mb-3 text-sm font-semibold tracking-wide uppercase">Overview</h3>
                        <div className="divide-y">
                            <DetailRow label="Reporter">{report.reporter?.name ?? report.reporter_name ?? 'Unknown'}</DetailRow>
                            <DetailRow label="Assignee">
                                {report.assignee?.name ?? <span className="text-muted-foreground">Unassigned</span>}
                            </DetailRow>
                            <DetailRow label="Module">{report.module ?? <span className="text-muted-foreground">—</span>}</DetailRow>
                            <DetailRow label="Created">{report.created_at ? new Date(report.created_at).toLocaleString() : '—'}</DetailRow>
                            <DetailRow label="Updated">{report.updated_at ? new Date(report.updated_at).toLocaleString() : '—'}</DetailRow>
                            <DetailRow label={t('github.label', 'GitHub')}>
                                {report.github_issue_url ? (
                                    <a
                                        href={report.github_issue_url}
                                        target="_blank"
                                        rel="noreferrer"
                                        className="text-primary inline-flex items-center gap-1 underline underline-offset-4"
                                    >
                                        {report.github_issue_number ? `#${report.github_issue_number}` : t('github.view_issue', 'View issue')}
                                        <ExternalLink className="h-3.5 w-3.5" />
                                    </a>
                                ) : (
                                    <span className="text-muted-foreground">{t('github.not_synced', 'Not synced')}</span>
                                )}
                            </DetailRow>
                            {report.duplicate_of && (
                                <DetailRow label="Duplicate of">
                                    <Link
                                        href={route('admin.bug-reports.show', report.duplicate_of.id)}
                                        className="text-primary underline underline-offset-4"
                                    >
                                        {report.duplicate_of.public_id ?? report.duplicate_of.id.slice(0, 8)}
                                    </Link>
                                </DetailRow>
                            )}
                        </div>
                    </div>

                    <div className="bg-card rounded-xl border p-5">
                        <div className="mb-3 flex items-center gap-2">
                            <ClipboardList className="text-muted-foreground h-4 w-4" />
                            <h3 className="text-sm font-semibold tracking-wide uppercase">Description</h3>
                        </div>
                        <p className="text-sm leading-7 whitespace-pre-wrap">{report.description}</p>
                        {report.expected_behavior && (
                            <div className="mt-4">
                                <p className="text-muted-foreground mb-1 text-xs font-semibold uppercase">Expected behavior</p>
                                <p className="text-sm leading-7 whitespace-pre-wrap">{report.expected_behavior}</p>
                            </div>
                        )}
                        {report.actual_behavior && (
                            <div className="mt-4">
                                <p className="text-muted-foreground mb-1 text-xs font-semibold uppercase">Actual behavior</p>
                                <p className="text-sm leading-7 whitespace-pre-wrap">{report.actual_behavior}</p>
                            </div>
                        )}
                    </div>

                    <div className="bg-card rounded-xl border p-5">
                        <div className="mb-3 flex items-center gap-2">
                            <Globe className="text-muted-foreground h-4 w-4" />
                            <h3 className="text-sm font-semibold tracking-wide uppercase">Context</h3>
                        </div>
                        <div className="divide-y">
                            <DetailRow label="URL">{report.url ?? <span className="text-muted-foreground">—</span>}</DetailRow>
                            <DetailRow label="Path">{report.path ?? <span className="text-muted-foreground">—</span>}</DetailRow>
                            <DetailRow label="Route name">{report.route_name ?? <span className="text-muted-foreground">—</span>}</DetailRow>
                            <DetailRow label="Entity">
                                {report.entity_type ? (
                                    `${report.entity_type} ${report.entity_id ?? ''}`
                                ) : (
                                    <span className="text-muted-foreground">—</span>
                                )}
                            </DetailRow>
                        </div>
                        {report.query_params_sanitized && (
                            <pre className="bg-muted mt-4 max-w-full overflow-x-auto rounded-lg p-3 text-xs">
                                {JSON.stringify(report.query_params_sanitized, null, 2)}
                            </pre>
                        )}
                    </div>

                    <div className="bg-card rounded-xl border p-5">
                        <div className="mb-3 flex items-center gap-2">
                            <Monitor className="text-muted-foreground h-4 w-4" />
                            <h3 className="text-sm font-semibold tracking-wide uppercase">Technical Details</h3>
                        </div>
                        <div className="divide-y">
                            <DetailRow label="Browser">{`${report.browser_name ?? 'Unknown'} ${report.browser_version ?? ''}`.trim()}</DetailRow>
                            <DetailRow label="OS">{report.os_name ?? <span className="text-muted-foreground">Unknown</span>}</DetailRow>
                            <DetailRow label="Device">{report.device_type ?? <span className="text-muted-foreground">Unknown</span>}</DetailRow>
                            <DetailRow label="Screen">{`${report.screen_width ?? '?'}×${report.screen_height ?? '?'}`}</DetailRow>
                            <DetailRow label="Viewport">{`${report.viewport_width ?? '?'}×${report.viewport_height ?? '?'}`}</DetailRow>
                            <DetailRow label="Locale / TZ">{`${report.locale ?? '—'} / ${report.timezone ?? '—'}`}</DetailRow>
                        </div>
                        {report.user_agent && <p className="text-muted-foreground mt-3 text-xs break-all">{report.user_agent}</p>}
                    </div>

                    <div className="bg-card rounded-xl border p-5">
                        <div className="mb-3 flex items-center gap-2">
                            <Paperclip className="text-muted-foreground h-4 w-4" />
                            <h3 className="text-sm font-semibold tracking-wide uppercase">Attachments</h3>
                        </div>
                        {report.attachments.length === 0 ? (
                            <p className="text-muted-foreground text-sm">No attachments.</p>
                        ) : (
                            <div className="space-y-2">
                                {report.attachments.map((attachment) => (
                                    <div key={attachment.id} className="flex flex-wrap items-center justify-between gap-2 rounded-lg border p-3">
                                        <div>
                                            <p className="text-sm font-medium">{attachment.file_name}</p>
                                            <p className="text-muted-foreground text-xs">
                                                {attachment.mime_type ?? 'file'}
                                                {attachment.file_size ? ` · ${(attachment.file_size / 1024).toFixed(1)} KB` : ''}
                                            </p>
                                        </div>
                                        <Button asChild variant="outline" size="sm">
                                            <a href={attachment.download_url} target="_blank" rel="noreferrer">
                                                Download
                                            </a>
                                        </Button>
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>

                    <div className="bg-card rounded-xl border p-5">
                        <h3 className="mb-4 text-sm font-semibold tracking-wide uppercase">Activity History</h3>
                        {report.events.length === 0 ? (
                            <p className="text-muted-foreground text-sm">No activity yet.</p>
                        ) : (
                            <div className="space-y-3">
                                {report.events.map((event) => (
                                    <div key={event.id} className="rounded-lg border p-3">
                                        <p className="text-sm font-medium">{event.event_type}</p>
                                        <p className="text-muted-foreground mt-0.5 text-xs">
                                            {event.actor ?? 'System'} · {event.created_at ? new Date(event.created_at).toLocaleString() : '—'}
                                        </p>
                                        {event.note && <p className="mt-2 text-sm">{event.note}</p>}
                                        {event.new_value && (
                                            <pre className="bg-muted mt-2 max-w-full overflow-x-auto rounded-lg p-2 text-xs">
                                                {JSON.stringify(event.new_value, null, 2)}
                                            </pre>
                                        )}
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>
                </div>
            </ShowLayout>
        </>
    );
}
